import { demoDialogTitleSx, demoDialogTextSx } from './dialog-styles'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonBase from '@mui/material/ButtonBase'
import { ThemeProvider } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { PlayArrow } from '@vedmarket/ui-kit'

import Calculator from '../calculator'
import DemoPlayerInputs from './player-inputs'
import DemoPlayerControls from './player-controls'
import DemoStepDialog from './step-dialog'
import DemoCompletionDialog from './completion-dialog'
import DemoIntroductionDialog from './introduction-dialog'
import { useDemoPlayerState } from './use-player-state'
import { useDemoPlayerSummary } from './use-player-summary'
import { useDemoPlayerInteractions } from './use-player-interactions'
import { useDemoPlayerScroll } from './use-player-scroll'
import { useDemoPlayerThemes } from './use-player-themes'
import { useDemoOverlayBounds } from './use-overlay-bounds'
import { useDemoCalculatorScale } from './use-calculator-scale'
import { DemoPlayerContext } from './player-clock'
import { COLORS } from '../../../config/constants'

const DemoPlayer = ({ scenario, introductionShown, onIntroductionComplete }: DemoPlayerProps) => {
  const { t } = useTranslation()

  const playerRef = useRef<HTMLDivElement>(null)
  const [ isFullscreen, setIsFullscreen ] = useState(false)

  const modalContainerRef = useRef<HTMLDivElement>(null)
  const modalLayerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLElement | null>(null)
  const screenRef = useRef<HTMLDivElement | null>(null)
  const { calculatorTheme, introductionTheme, toolbarTheme } = useDemoPlayerThemes({
    isFullscreen,
    modalContainerRef,
  })

  useDemoOverlayBounds(modalContainerRef)
  useDemoCalculatorScale({ contentRef, modalContainerRef, modalLayerRef })

  const {
    begin,
    clock,
    closeFinalOffer,
    completedStep,
    continuePlayback,
    currentStep,
    finalOfferVisible,
    finishSummary,
    handleRecalculatingChange,
    introduction,
    pause,
    paused,
    play,
    player,
    restartCurrentStep,
    run,
    seek,
    setSpeed,
    showPauseOverlay,
    showRecalculation,
    speed,
    summaryVisible,
    toolbarBlocked,
  } = useDemoPlayerState({ introductionShown, onIntroductionComplete })

  const playerContext = useMemo(() => ({ ...player, isFullscreen }), [ player, isFullscreen ])

  useDemoPlayerSummary({ clock, contentRef, onComplete: finishSummary, summaryVisible })
  useDemoPlayerInteractions({
    completedStep, contentRef, finalOfferVisible, introduction,
    pause,
    paused,
    play,
    screenRef,
    showRecalculation,
  })
  useDemoPlayerScroll({
    clock, completedStep, contentRef, finalOfferVisible,
    introduction, screenRef, showRecalculation,
  })

  useEffect(() => {
    const handleFullscreenChange = () => {
      const windowElement = playerRef.current?.closest('.demo__window')

      setIsFullscreen(document.fullscreenElement === windowElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleFullscreen = async () => {
    const windowElement = playerRef.current?.closest<HTMLElement>('.demo__window')

    try {
      if (document.fullscreenElement === windowElement) {
        await document.exitFullscreen()
      } else {
        await windowElement?.requestFullscreen()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <DemoPlayerContext.Provider value={playerContext}>
      <div ref={playerRef} className={introduction ? 'demo-player demo-player_intro' : 'demo-player'}>
        <div className='demo-player__screen' ref={element => {
          screenRef.current = element
          element?.toggleAttribute('inert', showRecalculation)
        }}>
          <main className='demo__content' ref={element => {
            contentRef.current = element
            element?.toggleAttribute('inert', paused)
          }}>
            <div className='demo-calculator-canvas'>
              <ThemeProvider theme={calculatorTheme}>
                <Calculator
                  key={run}
                  mainStepName={scenario.order.order_name}
                  testDriveScenarioSlug={scenario.slug}
                  shouldHideTitle
                />
              </ThemeProvider>
            </div>
          </main>
          <div className='demo-player__modal-layer' ref={modalLayerRef}>
            <div className='demo-calculator-overlays' ref={modalContainerRef} />
          </div>
          {showPauseOverlay && (
            <div className='demo-player__pause-overlay' data-demo-allowed='player'>
              <ButtonBase
                className='demo-player__play-button'
                aria-label={t('calc__demoPlayer__play')}
                onClick={play}
                sx={{
                  width: 128,
                  height: 128,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,.9)',
                  color: COLORS.WHITE,
                  '&:hover': {
                    backgroundColor: COLORS.BLACK_DARK
                  },
                  '@media (min-width: 768px) and (max-width: 1199px)': {
                    width: 96,
                    height: 96
                  },
                  '@media (min-width: 320px) and (max-width: 767px)': {
                    width: 64,
                    height: 64
                  }
                }}
              >
                <PlayArrow className='demo-player__play-icon' width={64} height={64} />
              </ButtonBase>
            </div>
          )}
          {completedStep !== null && (
            <ThemeProvider theme={introductionTheme}>
              <DemoStepDialog step={completedStep} onContinue={continuePlayback} />
            </ThemeProvider>
          )}
          {finalOfferVisible && (
            <ThemeProvider theme={introductionTheme}>
              <DemoCompletionDialog onClose={closeFinalOffer} />
            </ThemeProvider>
          )}
        </div>
        {!introduction && <div className='demo-player__toolbar' data-demo-allowed='player' ref={element => element?.toggleAttribute('inert', toolbarBlocked)}>
          <DemoPlayerControls paused={paused} step={currentStep} speed={speed} isFullscreen={isFullscreen} onToggle={paused ? play : pause} onSeek={seek} onSpeed={setSpeed} onFullscreen={handleFullscreen} />
          <ThemeProvider theme={toolbarTheme}>
            <DemoPlayerInputs scenario={scenario} onEdit={pause} onRecalculated={restartCurrentStep} onRecalculatingChange={handleRecalculatingChange} />
          </ThemeProvider>
        </div>}
        {showRecalculation && (
          <div className='demo-player__recalculation-overlay' data-demo-allowed='player'>
            <div className='demo-dialog' role='dialog' aria-modal='true' aria-labelledby='demo-recalculation-title'>
              <Typography
                id='demo-recalculation-title'
                component='h2'
                sx={demoDialogTitleSx}
              >
                {t('calc__demoPlayer__recalculatingTitle')}
              </Typography>
              <div className='demo-dialog__copy' role='status'>
                <Typography
                  sx={demoDialogTextSx}
                >
                  {t('calc__demoPlayer__recalculating')}
                </Typography>
              </div>
            </div>
          </div>
        )}
        {introduction && (
          <ThemeProvider theme={introductionTheme}>
            <DemoIntroductionDialog onBegin={begin} />
          </ThemeProvider>
        )}
      </div>
    </DemoPlayerContext.Provider>
  )
}

export default DemoPlayer
